import React, {
	SetStateAction,
	useEffect,
	useState,
} from 'react';
import { useParams } from 'react-router';
import {
	useMutation,
	UseMutationResult,
} from 'react-query';
import type { EstimateBunker, GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { EstimateProps } from '@api/models/estimate';
import {
	getFixtureDetails,
	getVessels,
	refreshEuaPrice,
} from '@client/lib/api';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import { ReturnResult } from './useCargoHandlers';

export type OnRefreshEuaPriceMutator = UseMutationResult<
	ReturnResult<typeof refreshEuaPrice>,
	Error,
	void
>;

type EstimateChanges = Partial<EstimateProps>;

export type OnUpdateEstimate = (
	attributes: Partial<EstimateProps> & {
		commencementBunkers?: Array<EstimateBunker>
		estimateIdOverride?: number;
	},
) => void;

export const useEstimateHandlers = ({
	selectedEstimateId: estimateIdProp,
	setEstimateGroupId,
	setSelectedEstimateId,
	remoteEstimate,
	syncEstimate,
	setSyncEstimate,
	refreshEverything,
	setLoading,
}: {
	selectedEstimateId: number | null;
	setEstimateGroupId: React.Dispatch<React.SetStateAction<number | null>>;
	setSelectedEstimateId: React.Dispatch<React.SetStateAction<number | null>>;
	remoteEstimate: GetEstimateDetailsResponse | null;
	syncEstimate: boolean;
	setSyncEstimate: React.Dispatch<SetStateAction<boolean>>;
	refreshEverything: () => Promise<void>;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
	const { id: estimateId, groupId: estimateGroupId } = useParams<{ id: string; groupId: string }>();

	const [localEstimateData, setLocalEstimateData] = useState<
		GetEstimateDetailsResponse | null
	>(null);

	const [allVessels, refreshAllVessels] = useFetchedState(getVessels, []);

	const [fixtureDetailsChecks] = useFetchedState(async () => {
		if (localEstimateData?.fixtureId == null || !localEstimateData.fixtureId) {
			return {
				fixtureHasInvoices: false,
				fixtureHasDisSyncedDAs: false,
			};
		}

		const detail = await getFixtureDetails(localEstimateData.fixtureId);

		return {
			fixtureHasInvoices: detail.hasHireInvoices,
			fixtureHasDisSyncedDAs: detail.hasDisSyncedDAs ?? false,
		};
	}, [localEstimateData]);

	const fixtureHasInvoices = fixtureDetailsChecks?.fixtureHasInvoices;
	const fixtureHasDisSyncedDAs = fixtureDetailsChecks?.fixtureHasDisSyncedDAs;

	const [
		pendingEstimateChanges,
		setPendingEstimateChanges,
	] = useState<EstimateChanges>({});

	useEffect(() => {
		const parsedId = Number(estimateId);
		const parsedGroupId = Number(estimateGroupId);

		if (parsedId !== null && estimateIdProp == null) {
			setSelectedEstimateId(parsedId);
		}

		if (parsedGroupId !== null) {
			setEstimateGroupId(parsedGroupId);
		}
	}, [estimateId, estimateIdProp, setSelectedEstimateId, estimateGroupId, setEstimateGroupId]);

	useEffect(() => {
		if (
			(localEstimateData == null && remoteEstimate != null) || syncEstimate
		) {
			setLocalEstimateData(remoteEstimate);
			setSyncEstimate(false);
		}
	}, [localEstimateData, remoteEstimate, setSyncEstimate, syncEstimate]);

	const onRefreshEuaPriceMutator: OnRefreshEuaPriceMutator = useMutation(
		{
			mutationFn: () => refreshEuaPrice(estimateIdProp!),
			onMutate: () => setLoading(true),
			onSuccess: async () => {
				showSuccessNotification('EUA price refreshed');
				refreshEverything();
				setLoading(false);
			},
		},
	);

	const onUpdateEstimate: OnUpdateEstimate = (
		attributes,
	) => {
		setPendingEstimateChanges((prev) => {
			return { ...prev, ...attributes };
		});

		setLocalEstimateData((prev) => {
			if (prev == null) {
				return null;
			}

			return { ...prev, ...attributes };
		});
	};

	return {
		estimate: localEstimateData,
		estimateResult: remoteEstimate,
		estimateLoading: false,
		onUpdateEstimate,
		onRefreshEuaPriceMutator,
		allVessels,
		refreshAllVessels,
		fixtureHasInvoices,
		fixtureHasDisSyncedDAs,
		pendingChanges: pendingEstimateChanges,
		setPendingChanges: setPendingEstimateChanges,
	};
};
